import React, { useRef, useState, useEffect, useCallback, forwardRef } from 'react';
import { View, Text } from '@smarterservices/smarter-ui';
import {
  usePlayerStore,
  getStore,
  useLayoutStore,
} from '../../../stores/StoreContext';
import PropTypes from 'prop-types';
import HlsViewer from '../../Common/HlsViewer';

// This component uses CSS positioning to show specific regions of the HLS video stream
// without using canvas drawing by sharing the MediaStream from the main video element
const Hls = forwardRef(
  (
    {
      SessionTrack,
      isFocusedTrack = false,
      width = '100%',
      height = '100%',
      style = {},
      ...props
    },
    ref
  ) => {
    const containerRef = useRef(null);
    const videoRef = useRef(null);
    const parentRef = useRef(null);
    const [viewPortSize, setViewPortSize] = useState({
      width: 500,
      height: 500,
    });
    const [resizeBasedOn, setResizeBasedOn] = useState('');
    const [containerStyle, setContainerStyle] = useState({});
    const [videoStyle, setVideoStyle] = useState({});
    const [maxVideoSize, setMaxVideoSize] = useState({ width: 0, height: 0 });
    const [parentSize, setParentSize] = useState({ width: 0, height: 0 });

    const PlayerStore = getStore('playerStore');
    const hlsVideoRef = usePlayerStore((state) => state.hlsVideoRef);
    const hlsMediaStream = usePlayerStore((state) => state.hlsMediaStream);

    // Memoize callbacks passed to HlsViewer to prevent new references on every render
    const handleHlsReady = useCallback(() => {
      PlayerStore.setHlsPlaybackReady(true);
    }, []);

    // Measure parent dimensions on mount
    useEffect(() => {
      if (!parentRef.current) return;

      const rect = parentRef.current.getBoundingClientRect();
      setParentSize({ width: rect.width, height: rect.height });
    }, []);
    const [videoHeight, setVideoHeight] = useState(100);
    const [positioning, setPositioning] = useState({
      left: 0,
      top: 0,
      width: 640,
      height: 360,
      sessionChangeTime: 0,
      id: 'default',
    });

    const isPlaying = usePlayerStore((state) => state.playing);
    const currentTime = usePlayerStore((state) => state.currentTime);
    const tracks = usePlayerStore((state) => state.tracks);
    const seekCounter = usePlayerStore((state) => state.seekCounter);
    const videoContainerSize = useLayoutStore(
      (state) => state.containers.video.size
    );
    const controlsContainerSize = useLayoutStore(
      (state) => state.containers.controls.size
    );

    // Expose the video element ref
    useEffect(() => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(videoRef.current);
        } else {
          ref.current = videoRef.current;
        }
      }
    }, [ref, videoRef]);

    // ensure the mute state is correct when a new video loads...
    useEffect(() => {
      if (!hlsVideoRef?.current) return;
      hlsVideoRef.current.muted = PlayerStore.muted;
    }, [hlsVideoRef, PlayerStore.muted]);

    // Cache sorted positions to avoid re-sorting on every currentTime tick
    const sortedPositionsRef = useRef([]);
    const positionBoundaryRef = useRef(0);
    const lastPositionEvalTimeRef = useRef(-1);
    const positionsSourceRef = useRef(null);

    // Re-sort only when the underlying positions array changes
    useEffect(() => {
      const positions = SessionTrack?.hls?.track?.positions;
      if (positions && positions !== positionsSourceRef.current) {
        positionsSourceRef.current = positions;
        sortedPositionsRef.current = [...positions].sort(
          (a, b) => a.sessionChangeTime - b.sessionChangeTime
        );
        // Reset boundaries to force re-evaluation
        positionBoundaryRef.current = 0;
        lastPositionEvalTimeRef.current = -1;
      }
    }, [SessionTrack?.hls?.track?.positions]);

    // Get the current positioning based on the current time
    const getCurrentPositioning = () => {
      const sorted = sortedPositionsRef.current;
      if (!sorted || sorted.length === 0) {
        return {
          left: 0,
          top: 0,
          width: 640,
          height: 360,
          sessionChangeTime: 0,
          id: 'default',
        };
      }

      let currentPosition = sorted[0];
      let nextBoundary = Infinity;

      for (let i = 0; i < sorted.length; i++) {
        if (sorted[i].sessionChangeTime <= currentTime) {
          currentPosition = sorted[i];
        } else {
          nextBoundary = sorted[i].sessionChangeTime;
          break;
        }
      }

      positionBoundaryRef.current = nextBoundary;
      lastPositionEvalTimeRef.current = currentTime;
      return currentPosition;
    };

    // Update positioning only when currentTime crosses a position boundary or seeks backward
    useEffect(() => {
      if (SessionTrack?.hls?.track?.positions?.length > 0) {
        const crossedForward = currentTime >= positionBoundaryRef.current;
        const seekedBackward = currentTime < lastPositionEvalTimeRef.current - 1;
        const isInitial = lastPositionEvalTimeRef.current === -1;

        if (!crossedForward && !seekedBackward && !isInitial) return;

        const newPosition = getCurrentPositioning();
        newPosition.aspectRatio = newPosition.height / newPosition.width;
        // Only update state when position actually changed to avoid cascading re-renders
        setPositioning((prev) => {
          if (prev.id === newPosition.id && prev.sessionChangeTime === newPosition.sessionChangeTime) {
            return prev;
          }
          return newPosition;
        });
      }
    }, [currentTime, SessionTrack]);

    // Use the shared MediaStream from PlayerStore instead of creating independent captureStream calls
    useEffect(() => {
      if (!videoRef.current) return;
      const ourVideo = videoRef.current;

      // Use shared stream from PlayerStore (created once in HlsVideoPlayer)
      if (hlsMediaStream && hlsMediaStream.active) {
        // Only reassign srcObject if it actually changed to avoid unnecessary DOM work
        if (ourVideo.srcObject !== hlsMediaStream) {
          ourVideo.srcObject = hlsMediaStream;
        }

        if (isPlaying && ourVideo.paused) {
          ourVideo
            .play()
            .catch((e) => console.log('Error playing video:', e));
        }
        return;
      }

      // Fallback: if shared stream not yet available, wait for it via the hlsVideoRef
      const mainVideoRef = hlsVideoRef;
      if (!mainVideoRef?.current) return;

      const mainVideo = mainVideoRef.current;
      const attachStream = () => {
        if (mainVideo.readyState >= 1 && typeof mainVideo.captureStream === 'function') {
          // Create shared stream if PlayerStore doesn't have one yet
          let stream = PlayerStore.hlsMediaStream;
          if (!stream || !stream.active) {
            stream = mainVideo.captureStream(60);
            PlayerStore.setHlsMediaStream(stream);
          }
          ourVideo.srcObject = stream;

          if (isPlaying && ourVideo.paused) {
            ourVideo
              .play()
              .catch((e) => console.log('Error playing video:', e));
          }
        }
      };

      if (mainVideo.readyState >= 1) {
        attachStream();
      } else {
        mainVideo.addEventListener('loadedmetadata', attachStream);
        return () => {
          mainVideo.removeEventListener('loadedmetadata', attachStream);
        };
      }
    }, [hlsVideoRef, hlsMediaStream, isPlaying]);

    // Sync play/pause state
    useEffect(() => {
      if (!videoRef.current) return;

      if (isPlaying && videoRef.current.paused) {
        videoRef.current
          .play()
          .catch((e) => console.log('Error playing video:', e));
      } else if (!isPlaying && !videoRef.current.paused) {
        videoRef.current.pause();
      }
    }, [isPlaying]);

    // Calculate the video style based on positioning and container size
    const updateVideoStyle = () => {
      if (!positioning || !viewPortSize.width || !viewPortSize.height) {
        setVideoStyle({
          position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        });
      }

      // The positioning data tells us which part of the video to show
      // We need to scale the video so that the region of interest fills the container

      // Calculate how much we need to scale the video
      const scaleWidth = viewPortSize.width / positioning.width;
      const scaleHeight = viewPortSize.height / positioning.height;

      // Use the same scale for both dimensions to maintain aspect ratio
      // We use the smaller scale to ensure the entire region fits in the viewport
      const scale = Math.min(scaleWidth, scaleHeight);

      // Calculate the dimensions of the entire video after scaling
      const videoWidth = PlayerStore.recordingDimensions.width * scale;
      const videoHeight = PlayerStore.recordingDimensions.height * scale;

      // Calculate the negative offset to position the region of interest
      const left = -(positioning.left * scale);
      const top = -(positioning.top * scale);

      // Update maxVideoSize
      setMaxVideoSize({
        width: positioning.width * scaleWidth,
        height: positioning.height * scaleHeight,
      });

      setVideoStyle({
        position: 'absolute',
        width: `${videoWidth}px`,
        height: `${videoHeight}px`,
        left: `${left}px`,
        top: `${top}px`,
      });
    };

    // Get the container style with aspect ratio preservation
    useEffect(() => {
      updateContainerDimensions();
    }, [positioning]);

    const updateContainerDimensions = () => {
      const style = {
        overflow: 'hidden',
        position: 'relative',
        margin: '0 auto',
      };
      //console.log('Parent size:', parentSize);
      //console.log('Positioning:', positioning);
      let containerDimensions = {};
      if (parentSize.width <= parentSize.height) {
        const adjustmentFactor = parentSize.width / positioning.width;

        containerDimensions = {
          width: positioning.width * adjustmentFactor,
          height: positioning.height * adjustmentFactor,
        };

        setResizeBasedOn('width');
      } else {
        const adjustmentFactor = parentSize.height / positioning.height;

        containerDimensions = {
          width: positioning.width * adjustmentFactor,
          height: positioning.height * adjustmentFactor,
        };

        setResizeBasedOn('height');
      }

      setViewPortSize(containerDimensions);
      setContainerStyle(style);
    };
    useEffect(() => {
      if (!videoContainerSize || !controlsContainerSize) return;
      const sidebarTrackCount = Object.keys(tracks).length - 1;
      if (isFocusedTrack) {
        const videoHeight =
          videoContainerSize.height - controlsContainerSize.height;
        const gapAdjustment = (sidebarTrackCount - 1) * 10;
        setVideoHeight(videoHeight + gapAdjustment);
      } else {
        const videoHeight =
          (videoContainerSize.height - controlsContainerSize.height) /
          sidebarTrackCount;
        //const gapAdjustment = (sidebarTrackCount - 1) * 5;
        setVideoHeight(videoHeight);
      }
    }, [videoContainerSize, controlsContainerSize]);

    return (
      <View
        as="div"
        id={SessionTrack?.id || 'viewport'}
        className={`hls-viewport ${resizeBasedOn}`}
        margin="auto"
        width={width}
        height={height}

        elementRef={(el) => {
          containerRef.current = el;
        }}
        style={{
          overflow: 'hidden',
          contain: 'strict',
        }}
        withVisualDebug={false}
      >
        {hlsVideoRef ? (
          <HlsViewer
            key={`hls-viewer-${SessionTrack?.id}-${isFocusedTrack ? 'focused' : 'sidebar'}-${seekCounter}`}
            hlsRef={hlsVideoRef}
            hlsMediaStream={hlsMediaStream}
            regionBounds={positioning}
            showDebug={PlayerStore.showHlsViewerDebug}
            width={width}
            height={height}
            sourceVideoWidth={PlayerStore.recordingDimensions.width}
            sourceVideoHeight={PlayerStore.recordingDimensions.height}
            margin="auto"
            onLoadedData={handleHlsReady}
            onLoadedMetadata={handleHlsReady}
            onCanPlay={handleHlsReady}
          />
        ) : (
          <></>
        )}
      </View>
    );
  }
);

Hls.propTypes = {
  SessionTrack: PropTypes.object.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
};

export default Hls;
